import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Outlet , useNavigate, useRouterState , Link }                    from '@tanstack/react-router';
import { LogOut, User, Building2, ChevronDown, Check, CalendarDays, CalendarSearch, Search, Crown, Zap, AlertTriangle, Lock, Package } from 'lucide-react';
import { useEffect, useState, useMemo } from 'react';
import { toast }              from 'sonner';


import { SupportWidget } from '@/components/cobrowsing/SupportWidget';
import { CommandSearch }     from '@/components/shared/command-search';
import { ThemeToggle }       from '@/components/shared/theme-toggle';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button }        from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input }         from '@/components/ui/input';
import { Label }         from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { tenantsApi, companiesApi, authApi, crmSubscriptionApi, forceLogout, jwtSv } from '@/lib/api';
import { useChatWidget } from '@/lib/chat-widget';
import { MONTHS_SHORT } from '@/lib/period';
import { computeAllowedIds } from '@/lib/plan-limits';
import { QK }            from '@/lib/query-client';
import { connectSocket, disconnectSocket, getSocket, subscribeToCompany, subscribeToTenant, emitActivity } from '@/lib/socket';
import { initials }      from '@/lib/utils';
import { useAppStore }   from '@/store/app.store';

import { Footer }            from './footer';
import { MobileBottomNav }   from './mobile-bottom-nav';
import { SetupPromptDialog } from './setup-prompt-dialog';
import { Sidebar }           from './sidebar';

function buildPeriods() {
  const now = new Date();
  // 1 mes futuro + mes actual + 47 meses atrás = 49 opciones (~4 años)
  return Array.from({ length: 49 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
    const v = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`;
    const l = `${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
    return { value: v, label: l };
  });
}

const PERIODS = buildPeriods();

export function AppLayout() {
  const {
    user, token, logout, setAuth,
    activeTenantId, activeCompanyId,
    companies,
    activePeriod, setActivePeriod,
    setTenants, setCompanies, setActiveCompany,
  } = useAppStore();

  useChatWidget();

  const [periodSearch,  setPeriodSearch]  = useState('');
  const [companySearch, setCompanySearch] = useState('');
  const [customPeriodOpen,  setCustomPeriodOpen]  = useState(false);
  const [customPeriodYear,  setCustomPeriodYear]  = useState('');
  const [customPeriodMonth, setCustomPeriodMonth] = useState('');

  const filteredPeriods = periodSearch.trim()
    ? PERIODS.filter(p => p.label.toLowerCase().includes(periodSearch.toLowerCase()))
    : PERIODS;

  function openCustomPeriod() {
    const now = new Date();
    setCustomPeriodYear(activePeriod?.slice(0, 4) || String(now.getFullYear()));
    setCustomPeriodMonth(activePeriod?.slice(4, 6) || String(now.getMonth() + 1).padStart(2, '0'));
    setCustomPeriodOpen(true);
  }

  function confirmCustomPeriod() {
    const y = Number(customPeriodYear);
    const m = Number(customPeriodMonth);
    if (!y || y < 2000 || y > 2100 || !m || m < 1 || m > 12) return;
    setActivePeriod(`${y}${String(m).padStart(2, '0')}`);
    setCustomPeriodOpen(false);
    setPeriodSearch('');
  }

  const filteredCompanies = companySearch.trim()
    ? companies.filter(c =>
        c.razonSocial.toLowerCase().includes(companySearch.toLowerCase()) ||
        c.ruc.includes(companySearch.trim())
      )
    : companies;

  // Computed fresh on every render — los getters del store se congelan con Object.assign
  const activeCompany = companies.find(c => c.id === activeCompanyId);
  const navigate     = useNavigate();
  const queryClient  = useQueryClient();

  // Refresh user profile if systemRole is missing (existing sessions pre-update)
  const { data: meData } = useQuery({
    queryKey: QK.me(),
    queryFn:  () => authApi.me().then(r => r.data),
    enabled:  !!token && !user?.systemRole,
    staleTime: Infinity,
  });
  useEffect(() => {
    if (meData && 'systemRole' in meData && token) {
      const updated = { ...user!, ...(meData as { systemRole: string }) };
      setAuth(token, updated);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meData]);

  // Load tenants
  const { data: tenants } = useQuery({
    queryKey: QK.tenants(),
    queryFn:  () => tenantsApi.list().then(r => r.data),
    enabled:  !!token,
  });

  // Plan CRM (for the header chip and company lock)
  const { data: crmPlan } = useQuery({
    queryKey: QK.crmPlan(),
    queryFn:  () => crmSubscriptionApi.getCurrent().then(r => r.data),
    enabled:  !!activeTenantId,
    staleTime: 5 * 60 * 1_000,
    retry: false,
  });

  const allowedCompanyIds = useMemo(
    () => computeAllowedIds(companies, crmPlan, undefined, user?.systemRole === 'MASTER'),
    [companies, crmPlan, user?.systemRole],
  );

  // Load companies for active tenant
  const { data: companiesData } = useQuery({
    queryKey: [...QK.companies(), activeTenantId],
    queryFn:  () => companiesApi.list().then(r => r.data),
    enabled:  !!activeTenantId,
    retry:    false,  // no reintentar — si falla es por tenantId stale, se corrige en setTenants
  });

  useEffect(() => { if (tenants) setTenants(tenants); },  [tenants,  setTenants]);
  useEffect(() => { if (companiesData) setCompanies(companiesData); }, [companiesData, setCompanies]);

  // WebSocket
  useEffect(() => {
    if (!token) return;
    connectSocket(token);
    return () => { /* don't disconnect on every re-render, only on logout */ };
  }, [token]);

  // Enforce single active session via real-time WebSocket notifications
  useEffect(() => {
    if (!token) return;
    const socket = getSocket();
    const handleSessionRevoked = (data: { newSessionVersion: number }) => {
      const currentSv = jwtSv(token);
      if (currentSv !== null && currentSv < data.newSessionVersion) {
        forceLogout('session_revoked');
      }
    };

    socket.on('session:revoked', handleSessionRevoked);
    return () => {
      socket.off('session:revoked', handleSessionRevoked);
    };
  }, [token]);

  useEffect(() => {
    if (activeTenantId)  subscribeToTenant(activeTenantId);
    if (activeCompanyId) subscribeToCompany(activeCompanyId);
  }, [activeTenantId, activeCompanyId]);

  // Navegación → presencia/historial en el CRM (ver events.gateway.ts `activity`)
  const routerState = useRouterState();
  const currentPath  = routerState.location.pathname;
  useEffect(() => {
    if (!token) return;
    emitActivity(currentPath);
  }, [token, currentPath]);

  const handleLogout = async () => {
    // Revoca el refresh token en el servidor antes de limpiar el estado local —
    // si no, la cookie httpOnly sigue viva y /auth/refresh vuelve a autenticar
    // al usuario en cuanto navega a una ruta protegida.
    try {
      await authApi.logout();
    } catch {
      // best-effort: si el server falla igual limpiamos la sesión local
    }
    logout();
    disconnectSocket();    // si no, el socket queda unido a las rooms tenant/company de la cuenta anterior
    queryClient.clear();   // elimina todos los datos cacheados del usuario anterior
    void navigate({ to: '/auth/login' });
    toast.success('Sesión cerrada correctamente');
  };

  return (
    <div className="flex h-full bg-background">
      <Sidebar />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top header */}
        <header className="flex h-14 items-center border-b bg-background px-2 sm:px-4 shrink-0 gap-1.5 sm:gap-3 safe-top">
          {/* ── Centro: empresa + período ─────────────────────── */}
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1 justify-start sm:justify-center">

            {/* Empresa */}
            {companies.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 h-9 sm:h-10 px-2 sm:px-3 rounded-lg border border-[#1f5d86]/25 bg-[#eaf2fa]/70 hover:bg-[#eaf2fa] hover:border-[#1f5d86]/50 transition-colors min-w-0 max-w-[180px] sm:max-w-[340px] group dark:bg-primary/5 dark:border-primary/20 dark:hover:bg-primary/10 outline-hidden focus-visible:ring-2 focus-visible:ring-[#1f5d86]/40">
                    <Building2 className="h-4 w-4 text-[#1f5d86] shrink-0" />
                    <div className="text-left min-w-0 flex-1">
                      <div className="text-[12px] sm:text-[13px] font-semibold truncate text-foreground leading-tight">
                        {activeCompany?.razonSocial ?? 'Empresa'}
                      </div>
                      <div className="hidden sm:block text-[11px] font-mono text-muted-foreground leading-tight">
                        {activeCompany ? `RUC ${activeCompany.ruc}` : 'Haz clic para seleccionar'}
                      </div>
                    </div>
                    <ChevronDown className="h-3.5 w-3.5 text-[#1f5d86]/60 shrink-0 group-hover:text-[#1f5d86] transition-colors" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-80 p-0" onCloseAutoFocus={() => { setCompanySearch(''); }}>
                  <DropdownMenuLabel className="text-xs text-muted-foreground font-normal px-3 pt-2 pb-1">
                    Selecciona la empresa activa
                  </DropdownMenuLabel>
                  {/* Buscador */}
                  <div className="px-2 pb-2 border-b">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                      <input
                        autoFocus
                        placeholder="Buscar por nombre o RUC..."
                        value={companySearch}
                        onChange={e => { setCompanySearch(e.target.value); }}
                        onKeyDown={e => { e.stopPropagation(); }}
                        className="w-full pl-7 pr-2 py-1.5 text-xs rounded-md border bg-background outline-hidden focus:ring-1 focus:ring-[#1f5d86]/40"
                      />
                    </div>
                  </div>
                  {/* Lista filtrada */}
                  <div className="max-h-72 overflow-y-auto">
                    {filteredCompanies.length === 0 ? (
                      <p className="py-4 text-center text-xs text-muted-foreground">Sin resultados</p>
                    ) : (
                      filteredCompanies.map(c => {
                        const allowed = allowedCompanyIds.has(c.id);
                        return (
                          <DropdownMenuItem
                            key={c.id}
                            disabled={!allowed}
                            onClick={() => { if (allowed) { setActiveCompany(c.id); setCompanySearch(''); } }}
                            className={allowed ? 'gap-3 cursor-pointer py-2.5 rounded-none' : 'gap-3 py-2.5 opacity-50 cursor-not-allowed rounded-none'}
                          >
                            <div className={`flex h-8 w-8 items-center justify-center rounded-md shrink-0 ${c.id === activeCompanyId && allowed ? 'bg-[#1f5d86]' : 'bg-muted'}`}>
                              {allowed
                                ? <Building2 className={`h-4 w-4 ${c.id === activeCompanyId ? 'text-white' : 'text-muted-foreground'}`} />
                                : <Lock className="h-4 w-4 text-muted-foreground/60" />
                              }
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium truncate">{c.razonSocial}</div>
                              <div className="text-xs text-muted-foreground font-mono">
                                {allowed ? `RUC ${c.ruc}` : 'Restringida por el plan'}
                              </div>
                            </div>
                            {c.id === activeCompanyId && allowed && (
                              <Check className="h-4 w-4 text-[#1f5d86] shrink-0" />
                            )}
                          </DropdownMenuItem>
                        );
                      })
                    )}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Separador */}
            {companies.length > 0 && <div className="hidden sm:block h-8 w-px bg-border shrink-0" />}

            {/* Período */}
            {companies.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1.5 sm:gap-2.5 h-9 sm:h-10 px-2 sm:px-3 rounded-lg border border-[#1f5d86]/25 bg-[#eaf2fa]/70 hover:bg-[#eaf2fa] hover:border-[#1f5d86]/50 transition-colors shrink-0 group dark:bg-primary/5 dark:border-primary/20 dark:hover:bg-primary/10 outline-hidden focus-visible:ring-2 focus-visible:ring-[#1f5d86]/40 disabled:opacity-40 disabled:pointer-events-none"
                    disabled={!activeCompany}
                  >
                    <CalendarDays className="h-4 w-4 text-[#1f5d86] shrink-0" />
                    <div className="text-left">
                      <div className="hidden sm:block text-[11px] text-muted-foreground leading-tight">Período</div>
                      <div className="text-[12px] sm:text-[13px] font-semibold text-foreground leading-tight">
                        {PERIODS.find(p => p.value === activePeriod)?.label ?? activePeriod}
                      </div>
                    </div>
                    <ChevronDown className="h-3.5 w-3.5 text-[#1f5d86]/60 shrink-0 group-hover:text-[#1f5d86] transition-colors" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-52 p-0" onCloseAutoFocus={() => { setPeriodSearch(''); }}>
                  {/* Buscador */}
                  <div className="p-2 border-b">
                    <div className="relative">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                      <input
                        autoFocus
                        placeholder="Ej: Mar 2024..."
                        value={periodSearch}
                        onChange={e => { setPeriodSearch(e.target.value); }}
                        onKeyDown={e => { e.stopPropagation(); }}
                        className="w-full pl-7 pr-2 py-1.5 text-xs rounded-md border bg-background outline-hidden focus:ring-1 focus:ring-[#1f5d86]/40"
                      />
                    </div>
                  </div>
                  {/* Lista filtrada */}
                  <div className="max-h-60 overflow-y-auto">
                    {filteredPeriods.length === 0 ? (
                      <p className="py-4 text-center text-xs text-muted-foreground">Sin resultados</p>
                    ) : (
                      filteredPeriods.map(p => (
                        <DropdownMenuItem
                          key={p.value}
                          onClick={() => { setActivePeriod(p.value); setPeriodSearch(''); }}
                          className="cursor-pointer justify-between rounded-none px-3 py-2"
                        >
                          <span className="text-sm">{p.label}</span>
                          {p.value === activePeriod && (
                            <Check className="h-4 w-4 text-[#1f5d86] shrink-0" />
                          )}
                        </DropdownMenuItem>
                      ))
                    )}
                  </div>
                  <DropdownMenuSeparator className="my-0" />
                  <DropdownMenuItem
                    onClick={openCustomPeriod}
                    className="cursor-pointer gap-1.5 rounded-none px-3 py-2 text-[#1f5d86] dark:text-primary font-medium"
                  >
                    <CalendarSearch className="h-3.5 w-3.5" />
                    <span className="text-sm">Otro período…</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <Dialog open={customPeriodOpen} onOpenChange={setCustomPeriodOpen}>
            <DialogContent className="sm:max-w-xs">
              <DialogHeader>
                <DialogTitle>Seleccionar período</DialogTitle>
              </DialogHeader>
              <form onSubmit={e => { e.preventDefault(); confirmCustomPeriod(); }} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Mes</Label>
                    <Select value={customPeriodMonth} onValueChange={setCustomPeriodMonth}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MONTHS_SHORT.map((m, i) => (
                          <SelectItem key={m} value={String(i + 1).padStart(2, '0')}>{m}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Año</Label>
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={2000}
                      max={2100}
                      value={customPeriodYear}
                      onChange={e => { setCustomPeriodYear(e.target.value); }}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => { setCustomPeriodOpen(false); }}>Cancelar</Button>
                  <Button type="submit">Seleccionar</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Derecha */}
          <div className="flex items-center justify-end gap-1 sm:gap-2 shrink-0">
            {/* Plan chip */}
            {crmPlan && (
              <Link to="/paquetes" search={{}}>
                {crmPlan.isExpired ? (
                  <span className="hidden sm:inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold bg-red-50 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-400 hover:opacity-80 transition-opacity">
                    <AlertTriangle className="h-3 w-3" />
                    Plan vencido
                  </span>
                ) : crmPlan.isFreePlan ? (
                  <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-[#e8a547] px-2.5 py-1 text-[11px] font-semibold bg-[#e8a547]/10 text-[#b07d10] dark:bg-[#e8a547]/15 dark:text-[#e8a547] hover:bg-[#e8a547]/20 transition-colors">
                    <Zap className="h-3 w-3 fill-current" />
                    Mejorar plan
                  </span>
                ) : (
                  <span className="hidden sm:inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold bg-[#eaf2fa] text-[#1f5d86] border-[#2980b9]/30 dark:bg-[#1f5d86]/20 dark:text-[#2980b9] hover:opacity-80 transition-opacity">
                    <Crown className="h-3 w-3" />
                    {crmPlan.activeSubscription?.product_name ?? crmPlan.planLabel}
                  </span>
                )}
              </Link>
            )}
            {/* Cmd+K search trigger */}
            <button
              onClick={() => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, bubbles: true }))}
              className="hidden md:flex items-center gap-2 h-8 px-3 rounded-md border text-xs text-muted-foreground hover:bg-accent transition-colors"
            >
              <Search className="h-3.5 w-3.5" />
              Buscar
              <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium">
                <span className="text-[10px]">⌘</span>K
              </kbd>
            </button>
            {/* En móvil/tablet: solo icono de búsqueda */}
            <Button
              variant="ghost" size="icon" className="md:hidden touch-target"
              aria-label="Buscar"
              onClick={() => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, bubbles: true }))}
            >
              <Search className="h-4 w-4" />
            </Button>

            <ThemeToggle />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-9 w-9 rounded-full touch-target">
                  <Avatar className="h-9 w-9">
                    {user?.avatarUrl ? (
                      <AvatarImage src={user.avatarUrl} className="object-cover" />
                    ) : null}
                    <AvatarFallback className="text-xs">
                      {user ? initials(`${user.firstName} ${user.lastName}`) : 'U'}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">{user?.firstName} {user?.lastName}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate({ to: '/configuracion' })}>
                  <User className="mr-2 h-4 w-4" /> Perfil
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate({ to: '/paquetes', search: {} })}>
                  <Package className="mr-2 h-4 w-4" /> Mi Suscripción
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                  <LogOut className="mr-2 h-4 w-4" /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          <Outlet />
        </main>

        <Footer />
      </div>

      {/* Bottom nav — mobile only (< lg) */}
      <MobileBottomNav />

      {/* Global Cmd+K search overlay */}
      <CommandSearch />

      {/* Smart setup prompts: shown when no company or no WhatsApp connected */}
      <SetupPromptDialog />

      <SupportWidget />
    </div>
  );
}
